// Atribución de la campaña de carrito abandonado.
//
// El CTA de esos mails apunta a la landing del evento con `?ac=<token>` (el id
// de la orden abandonada) y `?ac_step=`. Guardamos el token para que la compra
// se acredite a la campaña aunque no se cierre en esa misma visita: es lo
// normal que alguien abra el mail, mire, se vaya, y vuelva por su cuenta días
// después. Sin esto esa venta se lee como orgánica.
//
// Dónde vive cada cosa:
//   localStorage   — el token. Sobrevive al cierre del navegador, que es todo
//                    el punto.
//   sessionStorage — una marca de "el link se usó en esta visita", para poder
//                    distinguir la recuperación directa ('click') de la que
//                    llegó por el token guardado ('stored').

export const ABANDONED_CART_TOKEN_PARAM = 'ac';
export const ABANDONED_CART_STEP_PARAM = 'ac_step';

export type AbandonedCartStep = 'recovery' | 'urgency';
export type AbandonedCartAttribution = 'click' | 'stored';

export interface StoredAbandonedCart {
  token: string;
  step: AbandonedCartStep | null;
  /** Momento del click, en ms. Solo informativo: la validez la marca la fecha del evento. */
  ts: number;
}

export interface AbandonedCartAttributionData {
  token: string;
  step: AbandonedCartStep | null;
  attribution: AbandonedCartAttribution;
}

const storageKey = (eventId: string) => `${eventId}_abandoned_cart`;
const sessionKey = (eventId: string) => `${eventId}_abandoned_cart_session`;

const isStep = (value: string | null): value is AbandonedCartStep => (
  value === 'recovery' || value === 'urgency'
);

// Guarda el token que vino en la URL. Se llama en la landing del evento, que es
// donde aterriza el link del mail.
export const storeAbandonedCartToken = (
  eventId: string,
  token: string,
  step: string | null,
): void => {
  const payload: StoredAbandonedCart = {
    token,
    step: isStep(step) ? step : null,
    ts: Date.now(),
  };
  try {
    localStorage.setItem(storageKey(eventId), JSON.stringify(payload));
    // Marca la visita como venida del mail; sessionStorage muere al cerrar la
    // pestaña, que es exactamente la duración de "esta visita".
    sessionStorage.setItem(sessionKey(eventId), '1');
  } catch {
    // Modo incógnito o storage lleno: la atribución se pierde, la compra no.
  }
};

// Lee el token para mandarlo al crear la orden. Devuelve null si no hay nada
// guardado, si el dato está corrupto o si el evento ya pasó: la ventana de
// atribución dura hasta que arranca el evento, después el token no puede
// explicar ninguna venta.
export const readAbandonedCartAttribution = (
  eventId: string,
  eventDate: string,
): AbandonedCartAttributionData | null => {
  let raw: string | null = null;
  let fromThisVisit = false;
  try {
    raw = localStorage.getItem(storageKey(eventId));
    fromThisVisit = sessionStorage.getItem(sessionKey(eventId)) === '1';
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }

  const startsAt = new Date(eventDate).getTime();
  if (!Number.isNaN(startsAt) && startsAt < Date.now()) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<StoredAbandonedCart>;
    if (!parsed || typeof parsed.token !== 'string' || !parsed.token) {
      return null;
    }
    return {
      token: parsed.token,
      step: isStep(parsed.step ?? null) ? (parsed.step as AbandonedCartStep) : null,
      attribution: fromThisVisit ? 'click' : 'stored',
    };
  } catch {
    return null;
  }
};

// Se limpia junto con el resto del carrito cuando la compra se concreta, para
// que una segunda compra al mismo evento no se cuente otra vez como recuperada.
export const clearAbandonedCartToken = (eventId: string): void => {
  try {
    localStorage.removeItem(storageKey(eventId));
    sessionStorage.removeItem(sessionKey(eventId));
  } catch {
    // ignore storage errors
  }
};
